import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { add, differenceInMinutes, format, getWeekOfMonth, parse } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { compact, uniqBy } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { RktFormComponent, formDatepicker, formInput, formRow, formSelect, formTimepicker } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';

import { AbstractMoveMoneyBase, MoveMoneySchedulerRecurrenceModalComponent } from '@shared/components';
import { FormModel, MoveMoneyScheduleDetails } from '@shared/models';
import { formatDate, formatDateTimeToUTC, formatRecurrenceRule, systemTimeZone } from '@shared/utils';

interface MoveMoneySchedulerModel {
  isScheduleEnabled: boolean;
  isEdit: boolean;
  name: string;
  scheduleDate: string;
  scheduleTime: string;
  scheduleRecurring: string;
  alreadySentCount: number;
  scheduleRecurringDisplayValue: string;
}

const defaultRecurringOptions = [
  { value: '', label: 'Does not repeat' },
  { value: 'FREQ=DAILY;INTERVAL=1', label: 'Daily' },
  { value: 'FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,TU,WE,TH,FR', label: 'Every weekday (Monday to Friday)' },
  { value: 'CUSTOM', label: 'Custom' },
];

@Component({
  selector: 'app-move-money-scheduler',
  templateUrl: './move-money-scheduler.component.html',
  styleUrls: ['./move-money-scheduler.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RktIconComponent, FormsModule, ReactiveFormsModule, RktFormComponent],
})
export class MoveMoneySchedulerComponent implements OnInit, AfterViewChecked {
  @Input() initialValue?: Record<string, string | boolean | number>;

  @Output() changed = new EventEmitter<MoveMoneyScheduleDetails>();

  @Output() errored = new EventEmitter<boolean>();

  schedulerFormModel: FormModel<MoveMoneySchedulerModel> = {
    isScheduleEnabled: false,
    isEdit: false,
    name: undefined,
    scheduleDate: format(new Date(), 'yyyy-MM-dd'),
    scheduleTime: '00:00:00',
    scheduleRecurring: '',
    alreadySentCount: undefined,
    scheduleRecurringDisplayValue: undefined,
  };

  schedulerForm = new FormGroup({});

  scheduleRecurringOptions$ = new BehaviorSubject<{ label: string; value: string }[]>([...defaultRecurringOptions]);

  schedulerFormFields = [
    formRow([
      formInput({
        key: 'name',
        label: 'Nam\u0435',
        expressions: {
          'props.required': '!!model?.scheduleRecurring',
        },
      }),
    ]),
    formRow([
      formDatepicker({
        key: 'scheduleDate',
        label: 'Date',
        props: {
          placeholder: 'MM/DD/YYYY',
          minDate: formatDate(toZonedTime(new Date(), systemTimeZone)),
          maxDate: NgbDate.from({ day: 1, month: 1, year: new Date().getFullYear() + 120 }),
        },
        expressions: {
          'props.required': 'model?.isScheduleEnabled',
        },
      }),
      formTimepicker({
        key: 'scheduleTime',
        label: 'Time',
        expressions: {
          'props.required': 'model?.isScheduleEnabled',
        },
      }),
    ]),
    formRow([
      formSelect({
        key: 'scheduleRecurring',
        label: '',
        props: {
          options: this.scheduleRecurringOptions$.asObservable(),
        },
        expressions: {
          'props.disabled': '!model?.scheduleDate',
        },
      }),
    ]),
  ];

  startTimeError = '';

  customRecurringOption?: { value: string; label: string };

  constructor(
    private modalService: ModalService,
    private ref: ChangeDetectorRef,
    private moveMoneyService: AbstractMoveMoneyBase,
  ) {}

  ngOnInit() {
    if (this.initialValue) {
      this.schedulerFormModel = {
        ...this.schedulerFormModel,
        ...this.initialValue,
      };
    }
  }

  setInitialValues() {
    const scheduleDetails = this.moveMoneyService.scheduleDetails;

    if (scheduleDetails) {
      const { scheduleDateTime, scheduleRecurringDisplayValue, recurrenceRule, name, alreadySentCount } = scheduleDetails;
      const dateParsed = parse(scheduleDateTime!, 'MM/dd/yy HH:mm', new Date());
      const date = format(dateParsed, 'MM/dd/yyyy');
      const time = format(dateParsed, 'HH:mm:ss');

      this.schedulerFormModel = {
        ...this.schedulerFormModel,
        name,
        scheduleDate: date,
        scheduleTime: time,
        scheduleRecurring: recurrenceRule,
        alreadySentCount,
        scheduleRecurringDisplayValue,
      };

      if (recurrenceRule) {
        this.customRecurringOption = {
          value: recurrenceRule,
          label: recurrenceRule,
        };
      }

      this.onModelChange();
    }
  }

  ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  onModelChange() {
    const { scheduleTime, scheduleDate, isScheduleEnabled } = this.schedulerFormModel;
    if (isScheduleEnabled && scheduleTime && scheduleDate) {
      const scheduleDateParsed = format(parse(scheduleDate, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd');

      this.startTimeError =
        differenceInMinutes(toZonedTime(new Date(), systemTimeZone), new Date(`${scheduleDateParsed}T${scheduleTime}`)) > 0
          ? 'Start time cannot be before current time.'
          : '';
    }
    this.updateRecurringOptions();
    this.emitChanges();
  }

  onSchedulerEnabledToggle() {
    this.schedulerFormModel = {
      ...this.schedulerFormModel,
      isScheduleEnabled: !this.schedulerFormModel.isScheduleEnabled,
    };

    this.onModelChange();
    this.emitChanges();
  }

  emitChanges() {
    const { isScheduleEnabled, name, scheduleRecurring, scheduleDate, scheduleTime } = this.schedulerFormModel || {};
    if (!isScheduleEnabled) {
      this.changed.emit({});
      this.errored.emit(false);
      return;
    }

    this.changed.emit({
      scheduleDateTime: formatDateTimeToUTC({ date: scheduleDate, time: scheduleTime }),
      scheduleDateTimeDisplayValue: `${format(parse(scheduleDate!, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd')} ${scheduleTime}`,
      scheduleRecurringDisplayValue: formatRecurrenceRule(scheduleRecurring),
      recurrenceRule: scheduleRecurring,
      name,
      timeZone: 'UTC',
    });

    this.errored.emit((!!scheduleRecurring && !name) || !scheduleDate || !scheduleTime || !!this.startTimeError);
  }

  updateRecurringOptions() {
    const { scheduleDate, scheduleRecurring } = this.schedulerFormModel;

    if (scheduleRecurring === 'CUSTOM') {
      this.openCustomRecurrenceModal();
      this.setRecurringFieldValue();
      return;
    }

    const updatedOptions = this.getRecurringOptions(scheduleDate);

    if (!updatedOptions.find(({ value }) => value === scheduleRecurring)) {
      this.setRecurringFieldValue();
    }

    this.scheduleRecurringOptions$.next(updatedOptions);
  }

  openCustomRecurrenceModal() {
    const recurrenceModalRef = this.modalService.open(MoveMoneySchedulerRecurrenceModalComponent, {
      className: 'entity-form-modal schedule-recurrence-modal',
    });

    recurrenceModalRef.componentInstance.modalInitData({
      scheduleDate: this.schedulerFormModel.scheduleDate,
    });

    recurrenceModalRef.result.then(
      (result?: { value: string; label: string }) => {
        if (result) {
          this.customRecurringOption = result;
          this.setRecurringFieldValue(this.customRecurringOption.value);
          this.updateRecurringOptions();
        }
      },
      () => false,
    );
  }

  setRecurringFieldValue(value?: string) {
    this.schedulerFormModel = {
      ...this.schedulerFormModel,
      scheduleRecurring: value ?? '',
    };
    this.emitChanges();
  }

  getRecurringOptions(scheduleDate?: string) {
    const scheduleDateParsed = scheduleDate ? parse(scheduleDate, 'MM/dd/yyyy', new Date()) : undefined;

    if (!scheduleDateParsed) {
      return [...defaultRecurringOptions];
    }

    const dayOfWeek = format(scheduleDateParsed, 'EEEE');
    const dayOfWeekValue = format(scheduleDateParsed, 'EEEEEE').toUpperCase();
    const week = getWeekOfMonth(scheduleDateParsed);
    const isLastWeek = format(scheduleDateParsed, 'MMMM') !== format(add(scheduleDateParsed, { weeks: 1 }), 'MMMM');
    const weekLabels = ['first', 'second', 'third', 'fourth'];
    const weekLabel = isLastWeek ? 'last' : weekLabels[week - 1];
    const dayOfMonth = format(scheduleDateParsed, 'MMMM d');
    const dayValue = format(scheduleDateParsed, 'd');
    const monthValue = format(scheduleDateParsed, 'M');
    const weekValue = isLastWeek ? '-1' : week;

    return uniqBy(
      compact([
        { value: '', label: 'Does not repeat' },
        { value: 'FREQ=DAILY;INTERVAL=1', label: 'Daily' },
        { value: `FREQ=WEEKLY;INTERVAL=1;BYDAY=${dayOfWeekValue}`, label: `Weekly on ${dayOfWeek}` },
        {
          value: `FREQ=MONTHLY;INTERVAL=1;BYSETPOS=${weekValue};BYDAY=${dayOfWeekValue}`,
          label: `Monthly on the ${weekLabel} ${dayOfWeek}`,
        },
        { value: `FREQ=YEARLY;INTERVAL=1;BYMONTH=${monthValue};BYMONTHDAY=${dayValue}`, label: `Annually on ${dayOfMonth}` },
        { value: 'FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,TU,WE,TH,FR', label: 'Every weekday (Monday to Friday)' },
        this.customRecurringOption
          ? { value: this.customRecurringOption.value, label: formatRecurrenceRule(this.customRecurringOption?.value) }
          : undefined,
        { value: 'CUSTOM', label: 'Custom' },
      ]),
      'value',
    );
  }

  resetValue() {
    this.schedulerForm.reset();
    this.emitChanges();
  }
}
